import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import html2canvas from "html2canvas";
import { supabase } from '../config/supabaseClient';
import img from "../assets/images/new-logo.webp";
import certificateBg from "../../src/assets/images/certificate bg.png";
import "../assets/css/certificate.css";

const CertificateGenerator = ({
  mcqs,
  selectedAnswer,
  calculateResults,
  topicName,
  onShowResults,
  topicClass,
}) => {

  const [isCertificateVisible, setIsCertificateVisible] = useState(false);
  const [certificateData, setCertificateData] = useState({});
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);
  const [certificateGenerated, setCertificateGenerated] = useState(false);
  const [user, setUser] = useState(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [name, setName] = useState('');
  const [className, setClassName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const bgImage = new Image();
    bgImage.src = certificateBg;
    bgImage.crossOrigin = "anonymous";
    bgImage.onload = () => {
      setBgLoaded(true);
    };
    bgImage.onerror = () => {
      console.error("Failed to load background image");
    };
  }, []);

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const activeUser = session?.user ?? null;
      setUser(activeUser);
      if (activeUser) {
        setName(activeUser.user_metadata?.display_name || activeUser.email.split('@')[0]);
        setClassName(activeUser.user_metadata?.class || '');
      }
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      const activeUser = session?.user ?? null;
      setUser(activeUser);
      if (activeUser) {
        setName(activeUser.user_metadata?.display_name || activeUser.email.split('@')[0]);
        setClassName(activeUser.user_metadata?.class || '');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (bgLoaded && !certificateGenerated) {
      setShowNameModal(true);
    }
  }, [bgLoaded, certificateGenerated]);

  const handleDetailsSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name.");
      return;
    }

    const finalClassStr = topicClass ? topicClass.replace(/Class\s+/i, "").trim() : (user?.user_metadata?.class || "9");

    setIsSubmitting(true);
    try {
      let currentUserId;
      let finalName = name.trim();
      let guestEmail = '';

      if (user) {
        currentUserId = user.id;
        // Upsert user profile
        await supabase.from("profiles").upsert({
          id: currentUserId,
          name: finalName,
          class: finalClassStr,
          email: user.email
        });
      } else {
        // Sign up guest automatically behind the scenes
        guestEmail = `guest_${Date.now()}_${Math.floor(Math.random() * 1000)}@gramture.com`;
        const guestPassword = `GuestPass_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: guestEmail,
          password: guestPassword,
          options: {
            data: {
              display_name: finalName,
              class: finalClassStr
            }
          }
        });

        if (signUpError) throw signUpError;
        currentUserId = signUpData.user.id;

        // Insert guest profile record
        const { error: profileError } = await supabase.from("profiles").upsert({
          id: currentUserId,
          name: finalName,
          class: finalClassStr,
          email: guestEmail
        });
        
        if (profileError) throw profileError;
      }

      // Calculate results
      const correctAnswers = calculateResults();
      const score = (correctAnswers / mcqs.length) * 100;
      const complement = score >= 80 ? "Excellent" : score >= 50 ? "Good" : "Needs Improvement";

      // Fetch attempts
      let topicAttempts = 1;
      const { data: certData } = await supabase
        .from("certificates")
        .select("attempts")
        .eq("user_id", currentUserId)
        .eq("topic", topicName)
        .single();

      if (certData) {
        topicAttempts = (certData.attempts || 0) + 1;
      }
      setAttempts(topicAttempts);

      setCertificateData({
        topic: topicName.toUpperCase(),
        score: correctAnswers,
        complement,
        userName: finalName,
        attempts: topicAttempts,
      });

      // Insert Certificate record
      const certId = `${currentUserId}_${topicName}`;
      const { error: certError } = await supabase.from("certificates").upsert({
        id: certId,
        user_id: currentUserId,
        "userName": finalName,
        topic: topicName,
        score: correctAnswers,
        "totalQuestions": mcqs.length,
        complement,
        "issueDate": new Date().toISOString(),
        attempts: topicAttempts,
      });
      if (certError) console.error("Cert Error", certError);

      // Fetch topper attempts
      const { data: topperData } = await supabase
        .from("toppers")
        .select("totalAttempts")
        .eq("id", currentUserId)
        .single();

      let totalAttempts = (topperData ? (topperData.totalAttempts || 0) : 0) + 1;

      // Update Toppers Wall
      const { error: topperError } = await supabase.from("toppers").upsert({
        id: currentUserId,
        name: finalName,
        class: finalClassStr,
        score: correctAnswers,
        "totalQuestions": mcqs.length,
        topic: topicName,
        email: user ? user.email : guestEmail,
        "lastUpdated": new Date().toISOString(),
        "totalAttempts": totalAttempts,
      });
      if (topperError) console.error("Topper Error", topperError);

      setIsCertificateVisible(true);
      setCertificateGenerated(true);
      setShowNameModal(false);
      toast.success("Certificate generated and added to Toppers Wall!");
    } catch (err) {
      console.error("Error generating certificate:", err);
      toast.error("Failed to generate certificate. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadCertificate = () => {
    const certificateElement = document.querySelector("#certificate");
    const footer = document.querySelector("#certificate-footer");
    if (footer) footer.style.display = "none";

    html2canvas(certificateElement, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: null,
      scale: window.devicePixelRatio,
    }).then((canvas) => {
      const imageUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `${certificateData.userName}_certificate.png`;
      link.click();
      setIsDownloaded(true);
      if (footer) footer.style.display = "flex";
    }).catch((error) => {
      console.error("Error capturing certificate:", error);
      if (footer) footer.style.display = "flex";
    });
  };

  return (
    <div>
      <ToastContainer />
      
      {showNameModal && (
        <div style={modalStyles.modal}>
          <div style={modalStyles.modalContent}>
            <h2 style={modalStyles.title}>Enter Your Name</h2>
            <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1.5rem', lineHeight: '1.4' }}>
              To generate your certificate and claim your place on the Achievers' Wall, please enter your name.
            </p>
            <input
              type="text"
              placeholder="Your Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={modalStyles.input}
              disabled={isSubmitting}
            />
            <button
              onClick={handleDetailsSubmit}
              style={modalStyles.button}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Generating..." : "Generate Certificate"}
            </button>
          </div>
        </div>
      )}

      {isCertificateVisible && (
        <div id="certificate" className="certificate-container">
          <img
            src={certificateBg}
            alt="Certificate Background"
            className="certificate-bg-image"
          />
          <div className="certificate-content">
            <div className="certificate-header">
              <img src={img} alt="Logo" className="certificate-logo" />
              <h2 className="certificate-title">Certificate of Achievement</h2>
            </div>
            <div className="certificate-body">
              <h1 className="certificate-user-name">{certificateData.userName}</h1>
              <p className="certificate-body-text">Has successfully completed the course</p>
              <h3 className="certificate-topic">{certificateData.topic}</h3>
              <p className="certificate-body-text">
                with a score of <strong>{certificateData.score}</strong> out of {mcqs.length}
              </p>
              <p className="certificate-body-text">
                Evaluation: <strong>{certificateData.complement}</strong>
              </p>
              <p className="certificate-body-text">
                Attempt: <strong>{certificateData.attempts}</strong>
              </p>
              <p className="certificate-body-text certificate-issue-date">
                Issued on: <strong>{new Date().toLocaleDateString()}</strong>
              </p>
            </div>
            <div id="certificate-footer" className="certificate-footer">
              {!isDownloaded && (
                <button className="certificate-download-btn" onClick={handleDownloadCertificate}>
                  Download Certificate
                </button>
              )}
              <button className="certificate-download-btn" onClick={onShowResults}>
                View Correct Answers
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    background: "#ffffff",
    padding: "2.5rem 2rem",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "400px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
    textAlign: "center",
    fontFamily: "'Outfit', 'Inter', sans-serif"
  },
  title: {
    fontSize: "1.6rem",
    color: "#1e293b",
    marginBottom: "0.5rem",
    fontWeight: 800,
    letterSpacing: "-0.5px"
  },
  input: {
    display: "block",
    width: "100%",
    margin: "1rem 0",
    padding: "0.8rem 1rem",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    outline: "none",
    background: "#f8fafc",
    fontFamily: "'Outfit', 'Inter', sans-serif"
  },
  button: {
    width: "100%",
    padding: "0.85rem",
    background: "linear-gradient(135deg, #4f46e5, #3b82f6)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: 700,
    transition: "all 0.3s ease",
    boxShadow: "0 4px 14px rgba(59, 130, 246, 0.25)",
    marginTop: "0.5rem",
    fontFamily: "'Outfit', 'Inter', sans-serif"
  },
};

export default CertificateGenerator;